/**
 * Geometric gesture classifier for SignVoice Phase 2.
 *
 * 8 signs total:
 *   general:     hello (open hand), thanks (flat, thumb tucked), yes (fist),
 *                no (peace), help (thumbs up)
 *   hospitality: water (W handshape), bill (L handshape), table (flat together)
 *
 * All heuristics are region-agnostic (words come from the dictionary; geometry
 * is shared across ASL/LSM/LSP/LIBRAS/ISL for these 8 base poses).
 *
 * Each `THRESHOLD: ...` documents how to tune.
 */

// ---------------------- helpers ----------------------
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

const palmCenter = (lm) => ({
  x: (lm[0].x + lm[5].x + lm[9].x + lm[13].x + lm[17].x) / 5,
  y: (lm[0].y + lm[5].y + lm[9].y + lm[13].y + lm[17].y) / 5,
});

const palmLength = (lm) => dist(lm[0], lm[9]);

const isExtendedUp = (tip, mcp, margin = 0.05) => mcp.y - tip.y > margin;
const isFolded = (tip, mcp, margin = 0.0) => tip.y - mcp.y > margin;

// ============================================================
// General THRESHOLDS (Phase 1 refined)
// ============================================================
// THRESHOLD: TIP_PALM_DISTANCE_RATIO — un dedo se considera "extendido" si tip
//   está N veces más lejos del centro de la palma que su MCP. Bajar a 1.3 para
//   ser más permisivo, subir a 1.7 para ser más estricto.
const TIP_PALM_DISTANCE_RATIO = 1.5;

// THRESHOLD: FINGER_SPREAD_MIN_RATIO — separación mínima entre tips adyacentes,
//   normalizada por palmLength, para HOLA. Bajar a 0.10 si los dedos juntos
//   no clasifican como HOLA.
const FINGER_SPREAD_MIN_RATIO = 0.15;

// THRESHOLD: FINGER_TOGETHER_MAX_RATIO — separación MÁXIMA entre tips adyacentes
//   para que MESA clasifique correctamente. Cuanto más baja, más exigente
//   (dedos más pegados). Subir si MESA no se detecta con dedos casi juntos.
//   Debe ser < FINGER_SPREAD_MIN_RATIO para que exista una zona muerta
//   entre "juntos" (MESA) y "separados" (HOLA).
const FINGER_TOGETHER_MAX_RATIO = 0.10;

// THRESHOLD: THUMB_LIFT_MIN_RATIO — qué tan arriba debe estar el pulgar para AYUDA.
const THUMB_LIFT_MIN_RATIO = 0.7;

// THRESHOLD: FINGER_FOLD_RATIO — un dedo no-pulgar se considera cerrado si la
//   distancia tip→MCP es < ratio * palmLength. Subir a 0.6 si los puños no cierran.
const FINGER_FOLD_RATIO = 0.5;

// THRESHOLD: THUMB_ABOVE_REST_MIN — separación mínima del tip del pulgar sobre
//   el promedio de los demás tips para que AYUDA sea distinto de SÍ.
const THUMB_ABOVE_REST_MIN = 0.05;

// THRESHOLD: PALM_FLATNESS_MAX — varianza máxima en y de los 4 tips no-pulgar
//   para que GRACIAS clasifique como mano plana.
const PALM_FLATNESS_MAX = 0.04;

// THRESHOLD: THUMB_TUCK_RATIO — distancia max entre thumb tip y index MCP,
//   normalizada por palmLength. Usada por GRACIAS y WATER.
const THUMB_TUCK_RATIO = 0.40;

// THRESHOLD: PALM_UPPER_FRAME_MAX — y máximo del centroide de palma (proxy "cerca del mentón").
const PALM_UPPER_FRAME_MAX = 0.50;

// THRESHOLD: THANKS_TIP_RATIO — ratio tip-palm / mcp-palm para GRACIAS.
const THANKS_TIP_RATIO = 1.4;

// ============================================================
// Scorers — general
// ============================================================
function scoreOpenHand(lm) {
  const palmLen = palmLength(lm);
  if (palmLen < 0.001) return 0;
  const palm = palmCenter(lm);

  const tips = [4, 8, 12, 16, 20];
  const mcps = [2, 5, 9, 13, 17];
  let extendedCount = 0;
  for (let i = 0; i < tips.length; i++) {
    const tipD = dist(lm[tips[i]], palm);
    const mcpD = dist(lm[mcps[i]], palm);
    if (mcpD > 0 && tipD > mcpD * TIP_PALM_DISTANCE_RATIO) extendedCount++;
  }

  const spreads = [
    dist(lm[8], lm[12]) / palmLen,
    dist(lm[12], lm[16]) / palmLen,
    dist(lm[16], lm[20]) / palmLen,
  ];
  const allSpread = spreads.every((s) => s > FINGER_SPREAD_MIN_RATIO);

  return (extendedCount / 5) * (allSpread ? 1 : 0.6);
}

function scoreThanks(lm) {
  const palmLen = palmLength(lm);
  if (palmLen < 0.001) return 0;
  const palm = palmCenter(lm);

  const ys = [lm[8].y, lm[12].y, lm[16].y, lm[20].y];
  const meanY = ys.reduce((a, b) => a + b, 0) / 4;
  const varY = ys.reduce((s, y) => s + (y - meanY) ** 2, 0) / 4;
  const isFlat = varY < PALM_FLATNESS_MAX;

  const thumbTucked = dist(lm[4], lm[5]) < palmLen * THUMB_TUCK_RATIO;
  const upperHalf = palm.y < PALM_UPPER_FRAME_MAX;
  const tipExt = [
    dist(lm[8], palm) > dist(lm[5], palm) * THANKS_TIP_RATIO,
    dist(lm[12], palm) > dist(lm[9], palm) * THANKS_TIP_RATIO,
    dist(lm[16], palm) > dist(lm[13], palm) * THANKS_TIP_RATIO,
    dist(lm[20], palm) > dist(lm[17], palm) * THANKS_TIP_RATIO,
  ];
  const extendedCount = tipExt.filter(Boolean).length;

  let score = 0;
  if (isFlat) score += 0.25;
  if (thumbTucked) score += 0.30;
  if (upperHalf) score += 0.10;
  score += (extendedCount / 4) * 0.35;
  return score;
}

function scoreFist(lm) {
  const conds = [
    isFolded(lm[8], lm[5]),
    isFolded(lm[12], lm[9]),
    isFolded(lm[16], lm[13]),
    isFolded(lm[20], lm[17]),
    !isExtendedUp(lm[4], lm[2], 0.05),
  ];
  return conds.filter(Boolean).length / conds.length;
}

function scorePeace(lm) {
  const conds = [
    isExtendedUp(lm[8], lm[5]),
    isExtendedUp(lm[12], lm[9]),
    isFolded(lm[16], lm[13]),
    isFolded(lm[20], lm[17]),
    !isExtendedUp(lm[4], lm[2], 0.05),
  ];
  return conds.filter(Boolean).length / conds.length;
}

function scoreThumbsUp(lm) {
  const palmLen = palmLength(lm);
  if (palmLen < 0.001) return 0;
  const wrist = lm[0];
  const thumbTip = lm[4];
  const thumbMcp = lm[2];

  const thumbLifted =
    (thumbMcp.y - thumbTip.y) > palmLen * THUMB_LIFT_MIN_RATIO &&
    (wrist.y - thumbTip.y) > palmLen * THUMB_LIFT_MIN_RATIO;

  const folded = [
    dist(lm[8], lm[5]) < palmLen * FINGER_FOLD_RATIO,
    dist(lm[12], lm[9]) < palmLen * FINGER_FOLD_RATIO,
    dist(lm[16], lm[13]) < palmLen * FINGER_FOLD_RATIO,
    dist(lm[20], lm[17]) < palmLen * FINGER_FOLD_RATIO,
  ];
  const foldedCount = folded.filter(Boolean).length;

  const otherTipsAvgY = (lm[8].y + lm[12].y + lm[16].y + lm[20].y) / 4;
  const thumbAboveRest = thumbTip.y < otherTipsAvgY - THUMB_ABOVE_REST_MIN;

  let score = 0;
  if (thumbLifted) score += 0.4;
  score += (foldedCount / 4) * 0.4;
  if (thumbAboveRest) score += 0.2;
  return score;
}

// ============================================================
// Scorers — hospitality (Phase 2 new)
// ============================================================

/**
 * WATER — handshape "W": index + middle + ring extendidos arriba,
 * pinky plegado, thumb pegado a la palma.
 * Diferenciador clave vs NO: el anular está extendido.
 */
function scoreWater(lm) {
  const palmLen = palmLength(lm);
  if (palmLen < 0.001) return 0;
  const palm = palmCenter(lm);

  const conds = [
    dist(lm[8], palm) > dist(lm[5], palm) * TIP_PALM_DISTANCE_RATIO,    // index extended
    dist(lm[12], palm) > dist(lm[9], palm) * TIP_PALM_DISTANCE_RATIO,   // middle extended
    dist(lm[16], palm) > dist(lm[13], palm) * TIP_PALM_DISTANCE_RATIO,  // ring extended
    dist(lm[20], lm[17]) < palmLen * FINGER_FOLD_RATIO,                 // pinky folded
    dist(lm[4], lm[5]) < palmLen * THUMB_TUCK_RATIO,                    // thumb tucked
  ];
  return conds.filter(Boolean).length / conds.length;
}

/**
 * BILL — handshape "L": índice arriba + pulgar horizontal (separado, no arriba como AYUDA).
 * Los otros 3 dedos plegados.
 */
// THRESHOLD: BILL_THUMB_SEPARATION_MIN — distancia mínima thumb tip ↔ thumb MCP
//   para confirmar que el pulgar está extendido (no plegado). Bajar a 0.4 para
//   ser más permisivo.
const BILL_THUMB_SEPARATION_MIN = 0.5;
// THRESHOLD: BILL_THUMB_VERTICAL_MAX — si el pulgar está más arriba que esto
//   respecto a su MCP (normalizado por palmLen), se considera AYUDA, no BILL.
const BILL_THUMB_VERTICAL_MAX = 0.6;

function scoreBill(lm) {
  const palmLen = palmLength(lm);
  if (palmLen < 0.001) return 0;
  const palm = palmCenter(lm);

  const indexUp = dist(lm[8], palm) > dist(lm[5], palm) * TIP_PALM_DISTANCE_RATIO;
  const thumbExt = dist(lm[4], lm[2]) > palmLen * BILL_THUMB_SEPARATION_MIN;
  const thumbNotUp = (lm[2].y - lm[4].y) < palmLen * BILL_THUMB_VERTICAL_MAX;
  const middleFolded = dist(lm[12], lm[9]) < palmLen * FINGER_FOLD_RATIO;
  const ringFolded = dist(lm[16], lm[13]) < palmLen * FINGER_FOLD_RATIO;
  const pinkyFolded = dist(lm[20], lm[17]) < palmLen * FINGER_FOLD_RATIO;

  const conds = [indexUp, thumbExt, thumbNotUp, middleFolded, ringFolded, pinkyFolded];
  return conds.filter(Boolean).length / conds.length;
}

/**
 * TABLE — palma plana horizontal, 5 dedos extendidos pero JUNTOS.
 * Diferenciador clave vs HOLA: los dedos NO están separados.
 */
function scoreTable(lm) {
  const palmLen = palmLength(lm);
  if (palmLen < 0.001) return 0;
  const palm = palmCenter(lm);

  const tips = [4, 8, 12, 16, 20];
  const mcps = [2, 5, 9, 13, 17];
  let extendedCount = 0;
  for (let i = 0; i < 5; i++) {
    const tipD = dist(lm[tips[i]], palm);
    const mcpD = dist(lm[mcps[i]], palm);
    if (mcpD > 0 && tipD > mcpD * TIP_PALM_DISTANCE_RATIO) extendedCount++;
  }

  const togethers = [
    dist(lm[8], lm[12]) / palmLen,
    dist(lm[12], lm[16]) / palmLen,
    dist(lm[16], lm[20]) / palmLen,
  ];
  const allTogether = togethers.every((s) => s < FINGER_TOGETHER_MAX_RATIO);

  // Requiere 4/5 extendidos + dedos juntos para pasar threshold; 5/5 + juntos = 1.0.
  return (extendedCount / 5) * (allTogether ? 1 : 0.5);
}

// ============================================================
// Classifier
// ============================================================
const SCORERS = {
  hello: scoreOpenHand,
  thanks: scoreThanks,
  yes: scoreFist,
  no: scorePeace,
  help: scoreThumbsUp,
  water: scoreWater,
  bill: scoreBill,
  table: scoreTable,
};

export const CONFIDENCE_THRESHOLD = 0.8;

/**
 * Classify a frame of 21 hand landmarks.
 * @param {Array} landmarks — 21 normalized landmarks (x,y,z)
 * @param {Set<string>|null} allowedIds — if provided, only score these ids
 * @returns {{id, confidence}|null}
 */
export function classifyGesture(landmarks, allowedIds = null) {
  if (!landmarks || landmarks.length < 21) return null;
  let bestId = null;
  let bestScore = 0;
  for (const [id, fn] of Object.entries(SCORERS)) {
    if (allowedIds && !allowedIds.has(id)) continue;
    const s = fn(landmarks);
    if (s > bestScore) {
      bestScore = s;
      bestId = id;
    }
  }
  if (bestScore < CONFIDENCE_THRESHOLD) return null;
  return { id: bestId, confidence: Math.round(bestScore * 100) / 100 };
}

export const THRESHOLDS = {
  TIP_PALM_DISTANCE_RATIO,
  FINGER_SPREAD_MIN_RATIO,
  FINGER_TOGETHER_MAX_RATIO,
  THUMB_LIFT_MIN_RATIO,
  FINGER_FOLD_RATIO,
  THUMB_ABOVE_REST_MIN,
  PALM_FLATNESS_MAX,
  THUMB_TUCK_RATIO,
  PALM_UPPER_FRAME_MAX,
  THANKS_TIP_RATIO,
  BILL_THUMB_SEPARATION_MIN,
  BILL_THUMB_VERTICAL_MAX,
  CONFIDENCE_THRESHOLD,
};
